/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

export interface EnhanceGains {
  originalGain: number;
  enhancedSpeechGain: number;
  isolatedSpeechGain: number;
  backgroundGain: number;
}

export const computeTwoSliderGains = (
  enhancePercent: number,
  backgroundPercent: number,
  enhancementEnabled: boolean,
  modelVersion: string
): EnhanceGains => {
  if (!enhancementEnabled) {
    return {
      originalGain: 1.0,
      enhancedSpeechGain: 0.0,
      isolatedSpeechGain: 0.0,
      backgroundGain: 0.0,
    };
  }

  const enhancedGain = enhancePercent / 100.0;

  if (modelVersion == ModelVersion.v1) {
    let originalGain = 1.0 - enhancedGain;

    return {
      originalGain: originalGain,
      enhancedSpeechGain: enhancedGain,
      isolatedSpeechGain: 0.0,
      backgroundGain: 0.0,
    };
  } else if (modelVersion === ModelVersion.v2) {
    // equal gain crossfade bc they will be highly correlated
    let isolatedGain = 1 - enhancedGain;

    let originalGain = 0.0;

    // given 0-100 background strength percent, return 0-1 amplitude gain to apply
    const backgroundGainForStrength = (backgroundPercent: number): number => {
      if (backgroundPercent === 0.0) {
        return 0.0;
      } else if (backgroundPercent === 100.0) {
        return 1.0;
      } else {
        const backgroundRemovalStrength = 1.0 - backgroundPercent / 100.0;

        // this gives -9dB at the center point, which is approx half as loud as fullscale
        const minDBFS = -36.0;
        const backgroundDB = minDBFS * Math.pow(backgroundRemovalStrength, 2.0);
        const gainForDB = Math.pow(10, backgroundDB / 20);
        return gainForDB;
      }
    };
    let backgroundGain = backgroundGainForStrength(backgroundPercent);

    return {
      originalGain: originalGain,
      enhancedSpeechGain: enhancedGain,
      isolatedSpeechGain: isolatedGain,
      backgroundGain: backgroundGain,
    };
  } else {
    throw new Error(`Unsupported model version: ${modelVersion}`);
  }
};
